import axios from "axios";
import {
  CREATE_COMPLIANCE_RULE_URL,
  DELETE_COMPLIANCE_RULE_URL,
  UPDATE_COMPLIANCE_RULE_URL,
} from "@/utils/constants/constants";
import "datatables.net";
import "datatables.net-dt/css/dataTables.dataTables.css";
import $ from "jquery";

export default {
  methods: {
    initialize_modals() {
      const createRuleModalEl = this.$refs.createRuleModal;
      const deleteConfirmationModalEl = this.$refs.deleteConfirmationModal;

      if (createRuleModalEl && window.bootstrap) {
        this.createRuleModal = new window.bootstrap.Modal(createRuleModalEl, {
          backdrop: true,
          keyboard: true,
        });
      }

      if (deleteConfirmationModalEl && window.bootstrap) {
        this.deleteConfirmationModal = new window.bootstrap.Modal(deleteConfirmationModalEl, {
          backdrop: true,
          keyboard: true,
        });
      }
    },

    open_create_rule_modal() {
      this.is_editing = false;
      this.reset_rule_form();

      if (this.createRuleModal) {
        this.createRuleModal.show();
      }
    },

    open_edit_rule_modal(rule) {
      this.is_editing = true;
      this.populate_rule_form(rule);

      if (this.createRuleModal) {
        this.createRuleModal.show();
      }
    },

    close_rule_modal() {
      if (this.createRuleModal) {
        this.createRuleModal.hide();
      }
      this.reset_rule_form();
      this.is_editing = false;
    },

    confirm_delete_rule(rule) {
      this.rule_to_delete = rule;

      if (this.deleteConfirmationModal) {
        this.deleteConfirmationModal.show();
      }
    },

    close_delete_modal() {
      if (this.deleteConfirmationModal) {
        this.deleteConfirmationModal.hide();
        window.location.reload();
      }
      this.rule_to_delete = null;
    },

    populate_rule_form(rule) {
      const initialGraceDays = this.durationToDays(rule.initial_grace_period);
      const recurringGraceDays = this.durationToDays(rule.recurring_grace_period);
      const frequencyDays = this.durationToDays(rule.frequency);

      this.rule_form = {
        id: rule.id,
        name: rule.name || "",
        description: rule.description || "",
        initial_grace_period: "",
        frequency: "",
        recurring_grace_period: "",
        entity_type: rule.entity_type || sessionStorage.getItem("entity_type_id"),
        version: rule.version || 1,
        initial_grace_period_days: initialGraceDays,
        recurring_grace_period_days: recurringGraceDays,
        frequency_type: this.getFrequencyTypeFromDays(frequencyDays),
        custom_frequency_days:
          this.getFrequencyTypeFromDays(frequencyDays) === "custom" ? frequencyDays : null,
          logical_operator: rule.logical_operator || "AND",
      };
    },

    create_rule() {
      if (this.is_creating_rule) return;

      if (!this.token) {
        this.show_error("Authentication token is not available. Please refresh the page.");
        return;
      }

      if (!this.rule_form.name.trim()) {
        this.show_error("Please enter a rule name.");
        return;
      }

      if (this.rule_form.initial_grace_period_days < 0) {
        this.show_error("Initial grace period cannot be negative.");
        return;
      }

      if (this.rule_form.recurring_grace_period_days < 0) {
        this.show_error("Recurring grace period cannot be negative.");
        return;
      }

      if (
        this.rule_form.frequency_type === "custom" &&
        (!this.rule_form.custom_frequency_days || this.rule_form.custom_frequency_days < 1)
      ) {
        this.show_error("Please enter a valid custom frequency (minimum 1 day).");
        return;
      }

      this.is_creating_rule = true;

      const payload = {
        name: this.rule_form.name,
        description: this.rule_form.description,
        initial_grace_period: this.daysToDuration(this.rule_form.initial_grace_period_days),
        recurring_grace_period: this.daysToDuration(this.rule_form.recurring_grace_period_days),
        frequency: this.daysToDuration(this.calculated_frequency_days),
        entity_type: this.rule_form.entity_type,
        version: this.rule_form.version,
        logical_operator: this.rule_form.logical_operator,
        form_id: this.current_form_id,
      };

      axios
        .post(CREATE_COMPLIANCE_RULE_URL, payload, {
          headers: this.headers,
        })
        .then(() => {
          this.show_success("Compliance rule created successfully");
          this.close_rule_modal();
          window.location.reload();
        })
        .catch(error => {
          this.show_error(error.response?.data?.message || "Failed to create rule");
          if (error.response && error.response.status === 403) {
            this.refreshToken();
          }
        })
        .finally(() => {
          this.is_creating_rule = false;
        });
    },

    update_rule() {
      if (this.is_creating_rule || !this.rule_form.id) return;

      if (!this.token) {
        this.show_error("Authentication token is not available. Please refresh the page.");
        return;
      }

      if (!this.rule_form.name.trim()) {
        this.show_error("Please enter a rule name.");
        return;
      }

      if (this.rule_form.initial_grace_period_days < 0) {
        this.show_error("Initial grace period cannot be negative.");
        return;
      }

      if (this.rule_form.recurring_grace_period_days < 0) {
        this.show_error("Recurring grace period cannot be negative.");
        return;
      }

      if (
        this.rule_form.frequency_type === "custom" &&
        (!this.rule_form.custom_frequency_days || this.rule_form.custom_frequency_days < 1)
      ) {
        this.show_error("Please enter a valid custom frequency (minimum 1 day).");
        return;
      }

      this.is_creating_rule = true;

      const payload = {
        id: this.rule_form.id,
        name: this.rule_form.name,
        description: this.rule_form.description,
        initial_grace_period: this.daysToDuration(this.rule_form.initial_grace_period_days),
        recurring_grace_period: this.daysToDuration(this.rule_form.recurring_grace_period_days),
        frequency: this.daysToDuration(this.calculated_frequency_days),
        entity_type: this.rule_form.entity_type,
        version: this.rule_form.version,
        form_id: this.current_form_id,
        logical_operator: this.rule_form.logical_operator,
      };

      axios
        .post(UPDATE_COMPLIANCE_RULE_URL, payload, {
          headers: this.headers,
        })
        .then(() => {
          this.show_success("Compliance rule updated successfully");
          this.close_rule_modal();
          this.get_compliance_rules();
        })
        .catch(error => {
          this.show_error(error.response?.data?.message || "Failed to update rule");
          if (error.response && error.response.status === 403) {
            this.refreshToken();
          }
        })
        .finally(() => {
          this.is_creating_rule = false;
        });
    },

    delete_rule() {
      if (this.is_deleting || !this.rule_to_delete) return;

      if (!this.token) {
        this.show_error("Authentication token is not available. Please refresh the page.");
        return;
      }

      this.is_deleting = true;

      const payload = {
        rule_id: this.rule_to_delete.id,
      };

      axios
        .post(DELETE_COMPLIANCE_RULE_URL, payload, {
          headers: this.headers,
        })
        .then(() => {
          this.show_success("Compliance rule deleted successfully");
          this.close_delete_modal();
          this.get_compliance_rules();
        })
        .catch(error => {
          this.show_error(error.response?.data?.message || "Failed to delete rule");
          if (error.response && error.response.status === 403) {
            this.refreshToken();
          }
        })
        .finally(() => {
          this.is_deleting = false;
        });
    },

    reset_rule_form() {
      this.rule_form = {
        id: null,
        name: "",
        description: "",
        initial_grace_period: "",
        frequency: "",
        recurring_grace_period: "",
        entity_type: sessionStorage.getItem("entity_type_id"),
        version: 1,
        initial_grace_period_days: null,
        recurring_grace_period_days: null,
        frequency_type: null,
        custom_frequency_days: null,
        logical_operator: "AND",
      };
    },

    initialize_data_table() {
      const table_id = "#rule_management_table";

      this.$nextTick(() => {
        const table_element = document.querySelector(table_id);
        if (!table_element) return;

        if ($.fn.DataTable.isDataTable(table_id)) {
          $(table_id).DataTable().destroy();
        }

        this.dataTable = $(table_id).DataTable({
          responsive: true,
          autoWidth: false,
          pageLength: 10,
          columnDefs: [{ orderable: false, targets: [6] }],
        });
      });
    },
  },
};
